import React, { useEffect, useContext, useState } from "react";
import {
  Button,
  List,
  Typography,
  Badge,
  Space,
  Progress,
  Card,
  Divider,
} from "antd";
import { AppContext } from "src/context";
import { UploadOutlined, CloseOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";

import UploadFile from "../utils/upload";
import IndexedDBManager from "../utils/IndexedDBManager";
import {
  DB_NAME,
  DB_VERSION,
  FILE_STORE_NAME,
  FILE_STORE_CONFIG,
} from "src/config/db/config";

const BackgroundUploadQueue = () => {
  const { state } = useContext(AppContext);

  const [uploadIcon] = useState(<UploadOutlined />);
  const [fileList, setFileList] = useState<{ uid: string; file: File }[]>([]);
  const [progress, setProgress] = useState(0);
  const [visibleQueue, setVisibleQueue] = useState(false);

  const createDBManager = async (): Promise<IndexedDBManager> => {
    const manager = new IndexedDBManager(DB_NAME, DB_VERSION, [
      FILE_STORE_CONFIG,
    ]);
    await manager.init();
    return manager;
  };

  const uploadFilesSequentially = async (
    files: {
      uid: string;
      file: File;
      meta: {
        type: string;
        value: string;
        delete_previous?: boolean;
      };
    }[],
    manager: IndexedDBManager,
    index = 0,
    callback?: ((data: { source: string; hash: string }) => void) | undefined
  ): Promise<void> => {
    const current = files[index];
    if (current.meta.value != null) {
      const uploader = new UploadFile(
        current.file,
        setProgress,
        null,
        {
          type: current.meta.type,
          value: current.meta.value,
          delete_previous: current.meta.delete_previous || false,
        },
        callback
      );
      const status = await uploader.uploadComplete();

      if (status == 1) {
        await manager.delete(FILE_STORE_NAME, current.uid);
        await refreshFileList();
      }
    } else {
      await manager.delete(FILE_STORE_NAME, current.uid);
    }

    if (index + 1 < files.length) {
      await uploadFilesSequentially(files, manager, index + 1, callback);
    } else {
      await refreshFileList();
      setVisibleQueue(false);
    }
  };

  const refreshFileList = async () => {
    try {
      const manager = await createDBManager();
      const entries = await manager.getAll(FILE_STORE_NAME);
      const validFiles = entries.filter((e: any) => e.meta?.value);
      setFileList(
        validFiles.map((entry: any) => ({
          uid: entry.uid,
          file: entry.file,
        }))
      );

      if (validFiles.length > 0) {
        await uploadFilesSequentially(
          validFiles.map((entry: any) => ({
            uid: entry.uid,
            file: entry.file,
            meta: entry.meta,
          })),
          manager,
          0,
          state.data.callback
        );
      } else {
        setProgress(0);
        setVisibleQueue(false);
      }
    } catch (err) {
      console.warn("Failed to refresh upload queue:", err);
    }
  };

  const handleCancelFile = async (item: { uid: string; file: File }) => {
    try {
      const manager = await createDBManager();
      await manager.delete(FILE_STORE_NAME, item.uid);
      const updatedList = fileList.filter((ele) => ele.uid !== item.uid);
      setFileList(updatedList);

      if (updatedList.length === 0) {
        setVisibleQueue(false);
      } else {
        await refreshFileList();
      }
    } catch (err) {
      console.log("Error removing file:", err);
    }
  };

  useEffect(() => {
    refreshFileList();
  }, [state.data.reinit_upload]);

  useEffect(() => {
    refreshFileList();
  }, []);

  return (
    <>
      {fileList.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          whileHover={{ scale: 1.1, rotate: 3 }}
          whileTap={{ scale: 0.95, rotate: -3 }}
          style={{
            position: "fixed",
            bottom: 30,
            right: 30,
            zIndex: 1000,
          }}
        >
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={uploadIcon}
            onClick={() => setVisibleQueue(!visibleQueue)}
            style={{
              width: 40,
              height: 40,
              fontSize: 25,
              boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
              background: "linear-gradient(135deg, #1890ff, #40a9ff)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
            }}
          />
        </motion.div>
      )}

      <AnimatePresence>
        {visibleQueue && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            style={{
              position: "fixed",
              bottom: 80,
              right: 20,
              zIndex: 1000,
            }}
          >
            <Card
              title={
                <Typography.Text strong>
                  Uploading Files Queue ({fileList.length})
                </Typography.Text>
              }
              bordered
              style={{
                width: 320,
                maxHeight: 420,
                overflowY: "auto",
                borderRadius: 12,
              }}
              bodyStyle={{ padding: 16 }}
            >
              <List
                size="small"
                dataSource={fileList}
                renderItem={(item, index) => (
                  <motion.div
                    key={item.uid}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <List.Item
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Badge status="processing" text={item.file.name} />
                      <Space size="small">
                        {index === 0 && (
                          <Progress
                            percent={progress}
                            size="small"
                            strokeColor="#1890ff"
                            format={(percent) => `${percent}%`}
                            style={{ width: 80 }}
                          />
                        )}
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          danger
                          size="small"
                          onClick={() => handleCancelFile(item)}
                        />
                      </Space>
                    </List.Item>
                  </motion.div>
                )}
              />
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BackgroundUploadQueue;
